import React, { useState, useCallback } from 'react';
import ApprovalItem from '../ApprovalItem';
import styles from './style.module.scss';
import Modal from '../../../../../components/modal';
import ButtonIcon from '../../../../../components/ButtonIcon/ButtonIcon';
import toast from 'react-hot-toast';
import { getApprovalDescription, transformActionType } from '../../utils';

const PendingTab = ({ items, onAction, isExpanded, appData }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAction = useCallback((item, actionType) => {
    setSelectedItem(item);
    setAction(actionType);
    setShowModal(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedItem || !action) return;

    try {
      // For decline action, notes are required
      if (action === 'decline' && !notes.trim()) {
        toast.error('Notes are required when declining an approval', {
          duration: 3000,
          style: {
            background: '#FEF2F2',
            color: '#DC2626',
            border: '1px solid #FCA5A5'
          }
        });
        return;
      }

      if (action === 'approve' && selectedItem.action_type === 'soft_limit_breach') {
        if (!notes.trim()) {
          toast.error('Add approval notes before approving a soft limit pricing change.', {
            duration: 4000,
            style: {
              background: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FCA5A5'
            }
          });
          return;
        }
        const edits = selectedItem.edits_made;
        const hasEdits =
          edits &&
          typeof edits === 'object' &&
          !Array.isArray(edits) &&
          Object.keys(edits).length > 0;
        if (!hasEdits) {
          toast.error('This request has no pricing change data and cannot be approved.', {
            duration: 5000,
            style: {
              background: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FCA5A5'
            }
          });
          return;
        }
      }

      setIsSubmitting(true);
      await onAction(selectedItem.id, action, notes);
      
      // Reset form
      setShowModal(false);
      setSelectedItem(null);
      setAction('');
      setNotes('');
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedItem, action, notes, onAction]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setSelectedItem(null);
    setAction('');
    setNotes('');
  }, []);

  const handleNotesChange = useCallback((e) => {
    setNotes(e.target.value);
  }, []);

  const getModalTitle = () => {
    return action === 'approve' ? 'Approve Request' : 'Decline Request';
  };

  return (
    <>
      <div className={styles.tabContent}>
        {items.length > 0 ? (
          <div className={`${styles.itemsList} ${isExpanded ? styles.expanded : ''}`}>
            {items.map(item => (
              <ApprovalItem 
                key={item.id} 
                item={item}
                isPending={true}
                onApprove={() => handleAction(item, 'approve')}
                onDecline={() => handleAction(item, 'decline')}
                appData={appData}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>No pending items to display</div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={handleCancel} header={getModalTitle()}>
        <div className={styles.modalInner}>
          {selectedItem && (
            <div className={styles.itemDetails}>
              <div className={styles.actionType}>
                <span className={styles.metaLabel}>Type of Action</span>
                <span className={styles.value}>{transformActionType(selectedItem.action_type)}</span>
              </div>
              <div className={styles.description}>
                {getApprovalDescription(selectedItem)}
              </div>
            </div>
          )}
          <div className={styles.formGroup}>
            <label htmlFor="approval-notes">
              Notes
              {(action === 'decline' ||
                (action === 'approve' &&
                  selectedItem?.action_type === 'soft_limit_breach')) &&
                ' *'}
            </label>
            <textarea
              id="approval-notes"
              value={notes}
              onChange={handleNotesChange}
              placeholder={`Add ${action === 'approve' ? 'approval' : 'decline'} notes...`}
              required={action === 'decline'}
              rows={4}
            />
          </div>
          <div className={styles.footer}>
            <ButtonIcon
              type="button"
              textColor="#254FE2"
              bgColor="white"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </ButtonIcon>
            <ButtonIcon
              type="button"
              textColor="#FFFFFF"
              bgColor={action === 'approve' ? '#4C70EF' : '#E03B24'}
              onClick={handleSubmit}
              disabled={
                ((action === 'decline' ||
                  (action === 'approve' &&
                    selectedItem?.action_type === 'soft_limit_breach')) &&
                  !notes.trim()) ||
                isSubmitting
              }
              isLoading={isSubmitting}
            >
              {action === 'approve' ? 'Approve' : 'Decline'}
            </ButtonIcon>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PendingTab;